'use client'

import React from 'react'

import { chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import {
  ProgressBarTone,
  ProgressBarVariant,
  clampProgressValue,
  progressBarBodyStyles,
  progressBarFillStyles,
  progressBarFractionStyles,
  progressBarHeaderStyles,
  progressBarLabelStyles,
  progressBarPercentStyles,
  progressBarRootStyles,
  progressBarTrackStyles,
} from './ProgressBar.styles'

export type { ProgressBarTone, ProgressBarVariant }

export type ProgressBarProps = {
  value: number
  colorScheme?: ColorScheme
  variant?: ProgressBarVariant
  tone?: ProgressBarTone
  label?: string
  fractionLabel?: string
  showPercent?: boolean
  'aria-label'?: string
}

export const ProgressBar = ({
  value,
  colorScheme = 'dark',
  variant = 'full',
  tone = 'info',
  label,
  fractionLabel,
  showPercent = true,
  'aria-label': ariaLabel,
}: ProgressBarProps) => {
  const labelId = React.useId()
  const clampedValue = clampProgressValue(value)
  const percentLabel = `${Math.round(clampedValue)}%`
  const isLabelVisible = variant === 'full' && Boolean(label)

  // The role lives on the track, not the wrapper: progressbar prunes its
  // descendants from the a11y tree, which would hide the label and fraction.
  return (
    <chakra.div {...progressBarRootStyles(variant)}>
      {variant === 'full' && (label || fractionLabel) && (
        <chakra.div {...progressBarHeaderStyles}>
          {label && (
            <chakra.p id={labelId} {...progressBarLabelStyles(colorScheme)}>
              {label}
            </chakra.p>
          )}
          {fractionLabel && (
            <chakra.p {...progressBarFractionStyles(colorScheme)}>
              {fractionLabel}
            </chakra.p>
          )}
        </chakra.div>
      )}
      <chakra.div {...progressBarBodyStyles}>
        <chakra.div
          aria-label={ariaLabel ?? (isLabelVisible ? undefined : label)}
          aria-labelledby={!ariaLabel && isLabelVisible ? labelId : undefined}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={Math.round(clampedValue)}
          role='progressbar'
          {...progressBarTrackStyles(colorScheme, variant)}
        >
          <chakra.div {...progressBarFillStyles(colorScheme, tone, value)} />
        </chakra.div>
        {showPercent && (
          <chakra.p {...progressBarPercentStyles(colorScheme)}>
            {percentLabel}
          </chakra.p>
        )}
      </chakra.div>
    </chakra.div>
  )
}
